import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { notificationsApi, NotificationFilters } from '@/lib/api/notifications';
import { useNotificationStore } from '@/lib/notifications/store';

export function useNotifications(filters?: NotificationFilters) {
  const { t } = useTranslation();
  const { notifications, unreadCount, setNotifications, setUnreadCount, updateNotification } =
    useNotificationStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);
  const [markingAsReadIds, setMarkingAsReadIds] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
  });

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await notificationsApi.getAll(filters);
      setNotifications(response.data);
      setPagination({
        total: response.total,
        page: response.page,
        totalPages: response.totalPages,
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('notifications.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [filters?.page, filters?.limit, t]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsApi.getUnreadCount();
      setUnreadCount(response.count);
    } catch (err: any) {
      console.error('Error fetching unread count:', err);
    }
  }, [setUnreadCount]);

  const markAsRead = useCallback(
    async (id: string) => {
      // Prevent duplicate calls for already read notifications or currently being marked
      const notification = notifications.find((n) => n.id === id);
      if (!notification || notification.isRead || markingAsReadIds.has(id)) {
        return false;
      }

      try {
        setMarkingAsReadIds((prev) => new Set(prev).add(id));
        await notificationsApi.markAsRead(id);

        // Update the notification in the store
        updateNotification(id, { isRead: true });

        // Update unread count
        setUnreadCount(Math.max(0, unreadCount - 1));

        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('notifications.markAsReadError'));
        return false;
      } finally {
        setMarkingAsReadIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    },
    [notifications, markingAsReadIds, unreadCount, updateNotification, setUnreadCount, t]
  );

  const markAllAsRead = useCallback(async () => {
    // Check if there are any unread notifications before making API call
    const unreadNotifications = notifications.filter((n) => !n.isRead);
    if (unreadNotifications.length === 0) {
      return true; // No unread notifications, consider it successful
    }

    try {
      setIsMarkingAsRead(true);
      await notificationsApi.markAllAsRead();

      // Update all notifications in the store
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        isRead: true,
      }));
      setNotifications(updatedNotifications);

      // Reset unread count
      setUnreadCount(0);

      toast.success(t('notifications.markAllAsReadSuccess'));
      return true;
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('notifications.markAllAsReadError'));
      return false;
    } finally {
      setIsMarkingAsRead(false);
    }
  }, [notifications, setNotifications, setUnreadCount, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return {
    notifications,
    isLoading,
    isMarkingAsRead,
    markingAsReadIds,
    unreadCount,
    pagination,
    markAsRead,
    markAllAsRead,
    refreshNotifications: fetchNotifications,
    refreshUnreadCount: fetchUnreadCount,
  };
}

export function useUnreadNotifications() {
  const { unreadCount, setUnreadCount } = useNotificationStore();
  const [isLoading, setIsLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await notificationsApi.getUnreadCount();
      setUnreadCount(response.count);
    } catch (err: any) {
      console.error('Error fetching unread count:', err);
    } finally {
      setIsLoading(false);
    }
  }, [setUnreadCount]);

  useEffect(() => {
    fetchUnreadCount();

    // Refresh unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    isLoading,
    refreshUnreadCount: fetchUnreadCount,
  };
}
